import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, Check, ChevronDown, ChevronRight, Pencil, Plus, Search, Trash2, UserCog, Users, X } from "lucide-react";
import { format } from "date-fns";
import { useMemo, useState, useEffect } from "react";
import { UserFormDialog } from "@/components/admin/UserFormDialog";
import { CreateAdvisorDialog } from "@/components/admin/CreateAdvisorDialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

type SanitizedUser = {
  id: string;
  email: string;
  fullName: string;
  role: string;
  phoneNumber: string | null;
  phoneVerifiedAt: Date | null;
  mfaEnabled: boolean;
  mfaMethod: string;
  createdAt: Date;
  clientId: string | null;
  isFAndF: boolean;
};

export default function UserManagement() {
  const [location, setLocation] = useLocation();
  const [searchParams, setSearchParams] = useState(window.location.search);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SanitizedUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<SanitizedUser | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  
  const { data: users, isLoading, isError, error } = useQuery<SanitizedUser[]>({
    queryKey: ["/api/admin/users"],
    staleTime: 0,
  });

  useEffect(() => {
    setSearchParams(window.location.search);
  }, [location]);

  useEffect(() => {
    if (isError && error) {
      console.error("[UserManagement] Failed to fetch users:", error.message);
    }
  }, [isError, error]);

  const roleFilter = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.get('role');
  }, [searchParams]);

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    let result = roleFilter ? users.filter(user => user.role === roleFilter) : users;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(user =>
        user.fullName.toLowerCase().includes(q) ||
        user.email.toLowerCase().includes(q)
      );
    }
    return result;
  }, [users, roleFilter, searchQuery]);

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "administrator":
        return "default";
      case "advisor":
        return "secondary";
      case "client":
        return "outline";
      case "executor":
        return "outline";
      default:
        return "outline";
    }
  };

  const getRoleLabel = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getFilterTitle = () => {
    if (!roleFilter) return "All Users";
    return `${getRoleLabel(roleFilter)}s`;
  };

  const handleEditUser = (user: SanitizedUser) => {
    setEditingUser(user);
    setUserFormOpen(true);
  };

  const handleDeleteClick = (user: SanitizedUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest("DELETE", `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    },
  });

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete.id);
    }
  };

  const toggleFAndFMutation = useMutation({
    mutationFn: async ({ userId, isFAndF }: { userId: string; isFAndF: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/users/${userId}/f-and-f-flag`, { isFAndF });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "F&F tester flag updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update F&F flag", variant: "destructive" });
    },
  });

  const canEditOrDelete = (user: SanitizedUser) => {
    // Only allow editing/deleting advisors and clients
    return user.role === "advisor" || user.role === "client";
  };

  // ── Advisors section ──
  interface AdminAdvisor {
    id: string;
    fullName: string;
    email: string;
    companyName: string | null;
    profession: string | null;
    discountCode: string | null;
    linkedClientCount: number;
    createdAt: string;
  }

  interface AdminAdvisorDetail {
    id: string;
    fullName: string;
    email: string;
    companyName: string | null;
    profession: string | null;
    discountCode: string | null;
    linkedClients: { id: string; name: string }[];
    createdAt: string;
  }

  const [createAdvisorOpen, setCreateAdvisorOpen] = useState(false);
  const [expandedAdvisorId, setExpandedAdvisorId] = useState<string | null>(null);

  const { data: advisors = [] } = useQuery<AdminAdvisor[]>({
    queryKey: ["/api/admin/advisors"],
  });

  const { data: advisorDetail } = useQuery<AdminAdvisorDetail>({
    queryKey: [`/api/admin/advisors/${expandedAdvisorId}`],
    enabled: !!expandedAdvisorId,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">
            User Management
          </h1>
          <p className="text-muted-foreground mt-2">
            View and manage all system users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 w-48"
              data-testid="input-search-users"
            />
          </div>
          <Button
            onClick={() => {
              setEditingUser(null);
              setUserFormOpen(true);
            }}
            data-testid="button-add-user"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {roleFilter && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="px-3 py-1" data-testid="badge-active-filter">
            Filtered by: {getRoleLabel(roleFilter)}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation('/users')}
            data-testid="button-clear-filter"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filter
          </Button>
        </div>
      )}

      {/* ── Advisors card ── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-3">
            <UserCog className="h-5 w-5" />
            <CardTitle>Advisors ({advisors.length})</CardTitle>
          </div>
          <Button size="sm" onClick={() => setCreateAdvisorOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create Advisor
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {advisors.length === 0 ? (
            <p className="text-sm text-muted-foreground px-6 pb-6">No advisors yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Discount Code</TableHead>
                  <TableHead>Clients</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {advisors.map(adv => (
                  <>
                    <TableRow
                      key={adv.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedAdvisorId(expandedAdvisorId === adv.id ? null : adv.id)}
                    >
                      <TableCell className="font-medium">{adv.fullName}</TableCell>
                      <TableCell className="text-sm">{adv.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{adv.companyName ?? "—"}</TableCell>
                      <TableCell>
                        <span className="font-mono text-sm text-primary">{adv.discountCode ?? "—"}</span>
                      </TableCell>
                      <TableCell className="text-sm">{adv.linkedClientCount}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(adv.createdAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        {expandedAdvisorId === adv.id
                          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        }
                      </TableCell>
                    </TableRow>
                    {expandedAdvisorId === adv.id && (
                      <TableRow key={`${adv.id}-detail`}>
                        <TableCell colSpan={7} className="bg-muted/30 px-6 py-3">
                          {!advisorDetail ? (
                            <p className="text-sm text-muted-foreground">Loading...</p>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Linked Clients ({advisorDetail.linkedClients.length})
                              </p>
                              {advisorDetail.linkedClients.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No clients linked yet.</p>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {advisorDetail.linkedClients.map(c => (
                                    <Badge key={c.id} variant="outline" className="text-xs">{c.name}</Badge>
                                  ))}
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground pt-1">
                                Profession: {advisorDetail.profession?.replace(/_/g, " ") ?? "—"}
                              </p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateAdvisorDialog open={createAdvisorOpen} onOpenChange={setCreateAdvisorOpen} />

      <Card data-testid="card-users-table">
        <CardHeader className="flex flex-row items-center gap-3">
          <Users className="h-5 w-5" />
          <CardTitle>{getFilterTitle()}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading users...
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2 justify-center py-8 text-destructive" data-testid="text-users-error">
              <AlertCircle className="h-5 w-5" />
              <span>Failed to load users: {error?.message || "Unknown error"}</span>
            </div>
          ) : !filteredUsers || filteredUsers.length === 0 ? (
            <div className="text-center py-8">
              <div className="flex justify-center mb-3">
                <div className="rounded-full bg-muted p-3">
                  <Users className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
              <p className="font-medium">{roleFilter ? `No ${getRoleLabel(roleFilter).toLowerCase()}s found` : "No users found"}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {roleFilter ? "Try clearing the role filter or add a new user." : "Click \"Add User\" above to create the first user."}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>MFA Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      data-testid={`row-user-${user.id}`}
                    >
                      <TableCell className="font-medium" data-testid={`text-name-${user.id}`}>
                        {user.fullName}
                      </TableCell>
                      <TableCell data-testid={`text-email-${user.id}`}>
                        {user.email}
                      </TableCell>
                      <TableCell data-testid={`text-phone-${user.id}`}>
                        {user.phoneNumber || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getRoleBadgeVariant(user.role)}
                          data-testid={`badge-role-${user.id}`}
                        >
                          {getRoleLabel(user.role)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground text-xs">—</span>
                      </TableCell>
                      <TableCell data-testid={`text-mfa-${user.id}`}>
                        {user.mfaEnabled ? (
                          <Badge variant="default">Enabled ({user.mfaMethod})</Badge>
                        ) : (
                          <span className="text-muted-foreground">Disabled</span>
                        )}
                      </TableCell>
                      <TableCell data-testid={`text-created-${user.id}`}>
                        {user.createdAt ? format(new Date(user.createdAt), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canEditOrDelete(user) ? (
                            <>
                              {user.role === "client" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => toggleFAndFMutation.mutate({ userId: user.id, isFAndF: !user.isFAndF })}
                                  title={user.isFAndF ? "Remove F&F tester flag" : "Mark as F&F tester"}
                                >
                                  {user.isFAndF ? <span className="inline-flex items-center gap-1">F&amp;F<Check className="h-3 w-3" aria-hidden="true" /></span> : "F&F"}
                                </Button>
                              )}
                              <Button aria-label="Edit user"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditUser(user)}
                                data-testid={`button-edit-${user.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button aria-label="Delete user"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(user)}
                                data-testid={`button-delete-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <UserFormDialog
        open={userFormOpen}
        onOpenChange={(open) => {
          setUserFormOpen(open);
          if (!open) {
            setEditingUser(null);
          }
        }}
        user={editingUser}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="dialog-delete-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">{userToDelete?.fullName}</span>{" "}
              ({userToDelete?.role})? Their account and all associated data will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
